import {
  faCalendar,
  faClock,
  faUser
} from '@fortawesome/free-regular-svg-icons';
import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import EmptyList from 'components/common/EmptyList';
import FalconCloseButton from 'components/common/FalconCloseButton';
import Flex from 'components/common/Flex';
import Pagination from 'components/pagination';
import { useFormatDateTime } from 'hooks/useFormatDateTime';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { useEffect, useState } from 'react';
import { Button, Dropdown, Form, Modal, Spinner } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import CustomReportService from 'services/CustomReport.service';
import CustomReportLayout from './CustomReportLayout';

const CustomReportList = () => {
  const navigate = useNavigate();
  const formatDateTime = useFormatDateTime();
  const [reports, setReports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEmpty, setIsEmpty] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [integrationLink, setIntegrationLink] = useState('');
  const [executingReports, setExecutingReports] = useState(new Set());

  // Pagination state
  const [filters, setFilters] = useState({
    page: 0,
    size: 20,
    includeInactives: false
  });

  useEffect(() => {
    loadReports();
  }, [filters]);

  const loadReports = async () => {
    setIsLoading(true);
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const response = await CustomReportService.list(queryParams);

      if (response && response.content) {
        setReports(response);
        setIsEmpty(response.content.length === 0);
      }
    } catch (error) {
      toast.error('Erro ao carregar relatórios');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExecuteReport = async report => {
    const reportId = report.uuid;
    setExecutingReports(prev => new Set(prev).add(reportId));

    try {
      const response = await CustomReportService.execute(reportId);
      if (response && !response.ok) {
        toast.success('Relatório executado com sucesso');
        // Navigate to execution view
        navigate(`/reports/custom/${reportId}/view`);
      } else {
        toast.error('Erro ao executar relatório');
      }
    } catch (error) {
      toast.error('Erro ao executar relatório');
    } finally {
      setExecutingReports(prev => {
        const newSet = new Set(prev);
        newSet.delete(reportId);
        return newSet;
      });
    }
  };

  const handleEditReport = report => {
    navigate(`/reports/custom/${report.uuid}/edit`);
  };

  const handleViewReport = report => {
    navigate(`/reports/custom/${report.uuid}`);
  };

  const handleDeleteClick = report => {
    setReportToDelete(report);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!reportToDelete) return;

    try {
      await CustomReportService.delete(reportToDelete.uuid);
      toast.success('Relatório excluído com sucesso');
      loadReports();
    } catch (error) {
      toast.error('Erro ao excluir relatório');
    } finally {
      setShowDeleteModal(false);
      setReportToDelete(null);
    }
  };

  const handleGetIntegrationLink = report => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const link = `${baseUrl}/v1/custom-reports/integrations/${report.uuid}/execute?page={page}&size={size}`;
    setIntegrationLink(link);
    setShowLinkModal(true);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(integrationLink);
    toast.success('Link copiado para a área de transferência');
  };

  const getNextScheduleDate = report => {
    if (!report.schedule || report.schedule.inactive) {
      return 'Não agendado';
    }

    if (report?.schedule?.nextDate) {
      return formatDateTime(report.schedule.nextDate);
    }

    return 'Calculando...';
  };

  const columns = [
    {
      accessorKey: 'name',
      header: 'Nome do Relatório',
      meta: {
        headerProps: {
          className: 'text-900 fs-10',
          style: { width: '200px', maxWidth: '200px' }
        },
        cellProps: {
          style: {
            width: '200px',
            maxWidth: '200px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }
        }
      },
      cell: rowData => {
        const { uuid, name } = rowData.row.original;
        return (
          <Link
            to={`/reports/custom/${uuid}`}
            className="text-primary fw-semibold fs-10"
            title={name}
          >
            {name}
          </Link>
        );
      }
    },
    {
      accessorKey: 'schedule',
      header: 'Próxima Execução',
      meta: {
        headerProps: { className: 'text-900 fs-10', style: { width: '15%' } }
      },
      cell: rowData => {
        const { schedule } = rowData.row.original;
        return (
          <>
            {schedule && !schedule.inactive ? (
              <span className="fs-10">
                <FontAwesomeIcon icon={faClock} className="me-1" size="sm" />
                {formatDateTime(schedule.nextExecutionDate)}
              </span>
            ) : (
              <Flex alignItems="center" className="text-danger">
                <FontAwesomeIcon icon={faTimes} className="me-1" size="sm" />
                <span className="fs-10">Não agendado</span>
              </Flex>
            )}
          </>
        );
      }
    },
    {
      accessorKey: 'creationDate',
      header: 'Criado em',
      meta: {
        headerProps: { className: 'text-900 fs-10', style: { width: '15%' } }
      },
      cell: ({ getValue }) => (
        <Flex alignItems="center" className="fs-10">
          <FontAwesomeIcon
            icon={faCalendar}
            className="me-1"
            size="sm"
            color="text-muted"
          />
          <span className="fs-10">{formatDateTime(getValue())}</span>
        </Flex>
      )
    },
    {
      accessorKey: 'lastUpdateDate',
      header: 'Última Atualização',
      meta: {
        headerProps: { className: 'text-900 fs-10', style: { width: '15%' } }
      },
      cell: ({ getValue }) => (
        <Flex alignItems="center" className="fs-10">
          <FontAwesomeIcon icon={faClock} className="me-1" size="sm" />
          <span className="fs-10">{formatDateTime(getValue())}</span>
        </Flex>
      )
    },
    {
      accessorKey: 'user',
      header: 'Criado por',
      meta: {
        headerProps: { className: 'text-900 fs-10', style: { width: '15%' } }
      },
      cell: ({ getValue }) => {
        const user = getValue();
        return user ? (
          <Flex alignItems="center" className="fs-10">
            <FontAwesomeIcon icon={faUser} className="me-1" size="sm" />
            <span className="fs-10">{`${user.firstName} ${user.lastName}`}</span>
          </Flex>
        ) : (
          '-'
        );
      }
    },
    {
      accessorKey: 'inactive',
      header: 'Status',
      meta: {
        headerProps: { className: 'text-900 fs-10', style: { width: '15%' } }
      },
      cell: ({ row }) => (
        <Flex alignItems="center" className="fs-10">
          <FontAwesomeIcon
            icon={row.original.inactive ? faTimes : faCheck}
            size="sm"
            className={row.original.inactive ? 'text-danger' : 'text-success'}
          />
        </Flex>
      )
    },
    {
      accessorKey: 'actions',
      header: 'Ações',
      meta: {
        headerProps: {
          className: 'text-900 fs-10',
          style: { width: '15%' },
          className: 'text-end'
        }
      },
      enableSorting: false,
      cell: ({ row }) => {
        const report = row.original;
        const isExecuting = executingReports.has(report.uuid);

        return (
          <div className="text-end fs-10">
            <Button
              size="sm"
              variant="falcon-primary"
              className="me-1"
              onClick={() => handleExecuteReport(report)}
              disabled={isExecuting || report.inactive}
              title={
                report.inactive
                  ? 'Não é possível executar relatório inativo'
                  : 'Executar relatório'
              }
            >
              {isExecuting ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <FontAwesomeIcon icon="play" />
              )}
            </Button>

            <Dropdown className="d-inline fs-10" align="end">
              <Dropdown.Toggle
                size="sm"
                variant="falcon-default"
                className="dropdown-caret-none"
              >
                <FontAwesomeIcon icon="ellipsis-v" />
              </Dropdown.Toggle>

              <Dropdown.Menu>
                <Dropdown.Item onClick={() => handleViewReport(report)}>
                  <FontAwesomeIcon icon="eye" className="me-1" />
                  Ver Detalhes
                </Dropdown.Item>
                <Dropdown.Item onClick={() => handleEditReport(report)}>
                  <FontAwesomeIcon icon="edit" className="me-1" />
                  Editar
                </Dropdown.Item>
                <Dropdown.Item onClick={() => handleGetIntegrationLink(report)}>
                  <FontAwesomeIcon icon="link" className="me-1" />
                  Obter Link de Integração
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item
                  className="text-danger"
                  onClick={() => handleDeleteClick(report)}
                >
                  <FontAwesomeIcon icon="trash-alt" className="me-1" />
                  Excluir
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        );
      }
    }
  ];

  const table = useAdvanceTable({
    data: reports.content || [],
    columns,
    sortable: true,
    pagination: true,
    perPage: 20
  });

  return (
    <CustomReportLayout>
      {/* Header */}
      <Flex className="w-100 mb-3" justifyContent="between" alignItems="center">
        <Form.Select
          size="sm"
          className="rounded-pill fs-10"
          style={{ width: '180px' }}
          onChange={e =>
            setFilters(prevFilters => ({
              ...prevFilters,
              includeInactives: e.target.value
            }))
          }
        >
          <option value="true">Filtro por status</option>
          <option value="true">Todos</option>
          <option value="false">Somente ativos</option>
        </Form.Select>
        <Button
          variant="primary"
          className="fs-10"
          size="sm"
          onClick={() => navigate('/reports/custom/new')}
        >
          <FontAwesomeIcon icon="plus" className="me-1" />
          Novo Relatório
        </Button>
      </Flex>

      {/* Filters */}
      {/* <Row className="justify-content-end g-0 mb-3">
        <Col className="col-auto px-3">

        </Col>
      </Row> */}

      {isLoading ? (
        <div className="text-center py-5 fs-10">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : isEmpty ? (
        <EmptyList imgSize={500} vhSize={73} />
      ) : (
        <AdvanceTableProvider {...table}>
          <AdvanceTable
            tableProps={{
              striped: true
            }}
          />

          <div className="mt-3">
            <Pagination
              data={reports}
              filters={filters}
              setFilters={setFilters}
            />
          </div>
        </AdvanceTableProvider>
      )}

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header>
          <Modal.Title>Excluir Relatório</Modal.Title>
          <FalconCloseButton onClick={() => setShowDeleteModal(false)} />
        </Modal.Header>
        <Modal.Body>
          <p>
            Tem certeza de que deseja excluir o relatório "
            {reportToDelete?.name}"?
          </p>
          <p className="text-danger">Esta ação não pode ser desfeita.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="falcon-default"
            size="sm"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancelar
          </Button>
          <Button variant="danger" size="sm" onClick={handleDeleteConfirm}>
            Excluir Relatório
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Integration Link Modal */}
      <Modal
        show={showLinkModal}
        onHide={() => setShowLinkModal(false)}
        size="lg"
      >
        <Modal.Header>
          <Modal.Title>Link de Integração</Modal.Title>
          <FalconCloseButton onClick={() => setShowLinkModal(false)} />
        </Modal.Header>
        <Modal.Body>
          <p className="mb-4">
            Use este link para integrar a execução do relatório com sistemas
            externos. Configure corretamente a autenticação e os parâmetros
            conforme descrito abaixo.
          </p>

          <div className="mb-4">
            <h6 className="fw-bold mb-2">
              <FontAwesomeIcon icon="link" className="me-2 text-primary" />
              Endpoint de Integração
            </h6>
            <div className="bg-light p-3 rounded">
              <code className="text-break">{integrationLink}</code>
            </div>
          </div>

          <div className="mb-4">
            <h6 className="fw-bold mb-2">
              <FontAwesomeIcon icon="key" className="me-2 text-primary" />
              Autenticação (Obrigatório)
            </h6>
            <p className="mb-2">
              Adicione sua API Key no cabeçalho customizado da requisição:
            </p>
            <div className="bg-200 rounded p-3 mb-2">
              <code>X-API-key: {`\{sua_api_key}`}</code>
            </div>
            <div className="alert alert-warning py-2">
              <FontAwesomeIcon icon="exclamation-triangle" className="me-2" />
              <small>
                Sem a API Key no cabeçalho X-API-key, a requisição será
                rejeitada.
              </small>
            </div>
          </div>

          <div className="mb-4">
            <h6 className="fw-bold mb-2">
              <FontAwesomeIcon icon="cog" className="me-2 text-primary" />
              Método HTTP
            </h6>
            <div className="bg-200 rounded p-3">
              <code>GET</code>
            </div>
          </div>

          <div className="mb-4">
            <h6 className="fw-bold mb-2">
              <FontAwesomeIcon icon="list" className="me-2 text-primary" />
              Parâmetros de Paginação
            </h6>
            <p className="mb-2">
              Substitua os valores na URL conforme necessário:
            </p>
            <div className="bg-200 rounded p-3 mb-2">
              <div className="mb-1">
                <code>{`{page}`}</code> - Número da página (começando em 0)
              </div>
              <div>
                <code>{`{size}`}</code> - Quantidade de itens por página
              </div>
            </div>
            <div className="mt-2">
              <small className="text-muted">
                Exemplo: <code>?page=0&size=20</code>
              </small>
            </div>
          </div>

          <div className="alert alert-info">
            <FontAwesomeIcon icon="info-circle" className="me-2" />
            <strong>Exemplo completo de requisição:</strong>
            <br />
            <code>
              GET{' '}
              {integrationLink.replace('{page}', '0').replace('{size}', '20')}
            </code>
            <br />
            <small>Headers: X-API-key: {`\{sua_api_key}`}</small>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="falcon-default"
            size="sm"
            onClick={() => setShowLinkModal(false)}
          >
            Fechar
          </Button>
          <Button variant="primary" size="sm" onClick={copyToClipboard}>
            <FontAwesomeIcon icon="copy" className="me-1" />
            Copiar Link
          </Button>
        </Modal.Footer>
      </Modal>
    </CustomReportLayout>
  );
};

export default CustomReportList;
